import { useState, useRef } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { crearDisentimientoBac } from "../api/disentimientoAPI";
import { guardarDisentimientoOfflineBac } from "./disentimientoSync";
import { useOnlineStatus } from "../../hooks/useSync";
import { useBacAuth } from "../../context/BacAuthContext";
import { useTerritorio } from "../../context/TerritorioContext";
import { VINCULOS, fechaLocalHoy, horaLocalAhora, generarCodigoDisentimiento } from "./disentimientoComun";

// ════════════════════════════════════════════════════════════════════
// Nueva constancia de disentimiento — mismo formato oficial que el
// formulario público (/consentimiento_pai), dentro de BAC y offline:
// sin señal se guarda en el dispositivo y BAC la sube sola después.
// ════════════════════════════════════════════════════════════════════

// Quien vacuna registra varias constancias seguidas desde el mismo punto:
// se recuerdan el nombre y el cargo de quien informa. El municipio y la
// comunidad NO se piden: salen del territorio elegido en BAC.
const CLAVE_ULTIMO_PUNTO = "bac_disentimiento_ultimo_punto";
const leerUltimoPunto = () => {
  try { return JSON.parse(localStorage.getItem(CLAVE_ULTIMO_PUNTO)) || {}; } catch { return {}; }
};
const guardarUltimoPunto = (datos) => {
  try { localStorage.setItem(CLAVE_ULTIMO_PUNTO, JSON.stringify(datos)); } catch { /* no crítico */ }
};

const formularioInicial = (nombreUsuario) => {
  const ultimo = leerUltimoPunto();
  return {
    fecha_atencion: fechaLocalHoy(),
    hora_atencion: horaLocalAhora(),
    menor_nombre: "", menor_documento: "", menor_edad: "", menor_edad_unidad: "Años",
    representante_nombre: "", representante_documento: "",
    representante_vinculo: "", representante_vinculo_otro: "",
    representante_telefono: "", representante_correo: "",
    vacunas_ofrecidas: "", motivo_decision: "",
    // Por defecto, el usuario de BAC que está registrando
    profesional_nombre: ultimo.profesional_nombre || nombreUsuario || "",
    profesional_cargo: ultimo.profesional_cargo || "",
    no_firma: false, se_ofrecio_lectura_firma: "", testigo_nombre: "",
  };
};

export default function DisentimientoModal({ show, onHide, onGuardado }) {
  const { usuario } = useBacAuth();
  const online = useOnlineStatus();
  // Municipio y comunidad/barrio: los del territorio activo en BAC (el que
  // se ve en el encabezado y se cambia con "🔄 Cambiar territorio")
  const { data: territorioBac } = useTerritorio();
  // .trim(): algunos territorios del catálogo traen espacios al final ("MIRITI ")
  const municipio = (territorioBac?.territorio || "").trim();
  const comunidad = (territorioBac?.comunidad || "").trim();

  const [f, setF] = useState(() => formularioInicial(usuario?.nombre));
  const set = (campo, valor) => setF(p => ({ ...p, [campo]: valor }));
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // pantalla de confirmación

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const firmandoRef = useRef(false);
  const [hayFirma, setHayFirma] = useState(false);

  // El canvas se prepara cuando el modal YA terminó de abrirse (antes mide
  // 0×0) y cada vez que vuelve a mostrarse el formulario tras "Registrar
  // otra" — si no, la firma se dibuja en un canvas viejo y sale en blanco.
  const prepararCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#16302E";
    ctxRef.current = ctx;
  };

  const posicion = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const empezarFirma = (e) => {
    if (!ctxRef.current) prepararCanvas();
    if (!ctxRef.current) return;
    e.preventDefault();
    firmandoRef.current = true;
    setHayFirma(true);
    const p = posicion(e);
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(p.x, p.y);
  };
  const moverFirma = (e) => {
    if (!firmandoRef.current) return;
    e.preventDefault();
    const p = posicion(e);
    ctxRef.current.lineTo(p.x, p.y);
    ctxRef.current.stroke();
  };
  const terminarFirma = () => { firmandoRef.current = false; };
  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    if (canvas && ctxRef.current) ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
    setHayFirma(false);
  };

  const alternarNoFirma = (valor) => {
    if (valor) limpiarFirma();
    setF(p => ({ ...p, no_firma: valor, se_ofrecio_lectura_firma: "", testigo_nombre: "" }));
  };

  const validar = () => {
    if (!municipio) return "Primero elige el territorio y la comunidad en BAC (botón 🔄 Cambiar territorio)";
    if (!comunidad) return "Falta la comunidad o barrio: usa 🔄 Cambiar territorio en BAC y elige la comunidad";
    if (!f.fecha_atencion) return "Falta la fecha de atención";
    if (!f.menor_nombre.trim()) return "Falta el nombre del niño, niña o adolescente";
    if (!f.representante_nombre.trim() || !f.representante_documento.trim()) return "Completa nombre y documento de la madre, padre o representante";
    if (!f.representante_vinculo) return "Selecciona el vínculo o calidad de representación";
    if (f.representante_vinculo === "Otro" && !f.representante_vinculo_otro.trim()) return "Especifica el vínculo";
    if (f.representante_correo.trim() && !/^\S+@\S+\.\S+$/.test(f.representante_correo.trim())) return "El correo no parece válido";
    if (!f.vacunas_ofrecidas.trim()) return "Anota la(s) vacuna(s) y dosis indicada(s) en esta visita";
    if (!f.motivo_decision.trim()) return "Falta el motivo de la decisión";
    if (!f.profesional_nombre.trim()) return "Falta el nombre del profesional o vacunador que informó";
    if (f.no_firma) {
      if (!f.se_ofrecio_lectura_firma) return "Indica si se ofreció lectura y firma del formato";
      if (!f.testigo_nombre.trim()) return "Falta el nombre del testigo";
    } else if (!hayFirma) {
      return "Falta la firma";
    }
    return null;
  };

  const guardar = async () => {
    const error = validar();
    if (error) {
      Swal.fire({ icon: "warning", title: error, timer: 2400, showConfirmButton: false });
      return;
    }

    setEnviando(true);
    try {
      const vinculo = f.representante_vinculo === "Otro" ? f.representante_vinculo_otro.trim() : f.representante_vinculo;
      const tieneEdad = f.menor_edad !== "";
      const payload = {
        codigo: generarCodigoDisentimiento(),
        municipio,
        comunidad: comunidad || null,
        fecha_atencion: f.fecha_atencion,
        hora_atencion: f.hora_atencion || null,
        menor_nombre: f.menor_nombre.trim(),
        menor_documento: f.menor_documento.trim() || null,
        menor_edad: tieneEdad ? Number(f.menor_edad) : null,
        menor_edad_unidad: tieneEdad ? f.menor_edad_unidad : null,
        representante_nombre: f.representante_nombre.trim(),
        representante_documento: f.representante_documento.trim(),
        representante_vinculo: vinculo,
        representante_telefono: f.representante_telefono.trim() || null,
        representante_correo: f.representante_correo.trim() || null,
        vacunas_ofrecidas: f.vacunas_ofrecidas.trim(),
        motivo_decision: f.motivo_decision.trim(),
        firma_base64: f.no_firma ? null : canvasRef.current.toDataURL("image/png"),
        profesional_nombre: f.profesional_nombre.trim(),
        profesional_cargo: f.profesional_cargo.trim() || null,
        se_ofrecio_lectura_firma: f.no_firma ? f.se_ofrecio_lectura_firma : null,
        testigo_nombre: f.no_firma ? f.testigo_nombre.trim() : null,
      };

      guardarUltimoPunto({
        profesional_nombre: f.profesional_nombre.trim(),
        profesional_cargo: f.profesional_cargo.trim(),
      });

      let quedoOffline = false;
      if (!online) {
        await guardarDisentimientoOfflineBac(payload);
        quedoOffline = true;
      } else {
        try {
          await crearDisentimientoBac(payload);
        } catch (errEnLinea) {
          // Si el servidor respondió con un error de datos, hay que
          // corregirlo — no tiene caso guardarlo para después. Si NO hubo
          // respuesta (sin señal, tiempo agotado), se guarda en el
          // dispositivo para no perder lo que ya se llenó.
          if (errEnLinea.response) throw errEnLinea;
          await guardarDisentimientoOfflineBac(payload);
          quedoOffline = true;
        }
      }

      // Avisa a la tabla y al contador "☁️ por sincronizar" de BAC
      window.dispatchEvent(new CustomEvent("disentimiento-actualizado"));
      window.dispatchEvent(new CustomEvent("sync-completado"));
      onGuardado && onGuardado();

      setResultado({
        offline: quedoOffline,
        menor: payload.menor_nombre,
        representante: payload.representante_nombre,
        municipio: payload.municipio,
        comunidad: payload.comunidad,
        correo: payload.representante_correo,
      });
    } catch (err) {
      Swal.fire("No se pudo registrar", err.response?.data?.message || "Intenta de nuevo en un momento.", "error");
    } finally {
      setEnviando(false);
    }
  };

  const reiniciar = () => {
    setF(formularioInicial(usuario?.nombre));
    setHayFirma(false);
    ctxRef.current = null;
    setResultado(null);
    // El canvas vuelve a montarse en el siguiente render
    setTimeout(prepararCanvas, 50);
  };

  const cerrar = () => {
    if (enviando) return;
    if (resultado) reiniciar();
    onHide();
  };

  return (
    <Modal show={show} onHide={cerrar} onEntered={prepararCanvas} fullscreen="sm-down" size="lg" scrollable backdrop="static" contentClassName="dis-modal-content">
      <Modal.Header closeButton style={{ background: "linear-gradient(160deg, #0E4F57, #146873)", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700 }}>
          Constancia de disentimiento informado frente a la vacunación
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="form-disentimiento-bac" style={{ background: "#FBF9F4" }}>
        <style>{`
          .form-disentimiento-bac { text-align: left; }
          /* EvaluacionRiesgoModal.css (SAT) le pone padding y alto máximo a TODOS
             los .modal-content de la app — aquí se anula solo para este modal */
          .dis-modal-content { padding: 0 !important; max-height: none !important; overflow: hidden !important; max-width: none !important; }
          @media (min-width: 576px) { .dis-modal-content { max-height: calc(100vh - 3.5rem) !important; } }
          .form-disentimiento-bac .form-control,
          .form-disentimiento-bac .form-select {
            border-radius: 12px; border: 1.5px solid #DDE5E0; padding: 10px 13px; font-size: 15px;
          }
          .form-disentimiento-bac .form-control:focus,
          .form-disentimiento-bac .form-select:focus {
            border-color: #2BA8C4; box-shadow: 0 0 0 3px rgba(43,168,196,0.15);
          }
          .form-disentimiento-bac .form-control::placeholder { color: #A9B6B1; }
        `}</style>

        {resultado ? (
          <div style={{ maxWidth: 420, margin: "0 auto", padding: "30px 6px", textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: resultado.offline ? "#FCE7DA" : "#E5F3E3", color: resultado.offline ? "#CC5A26" : "#2C6B2F", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", fontSize: 30 }}>
              {resultado.offline ? "☁️" : "✓"}
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#16302E", marginBottom: 10 }}>
              {resultado.offline ? "Constancia guardada en el dispositivo" : "Constancia registrada"}
            </h2>
            <p style={{ fontSize: 14, color: "#4C6360", marginBottom: 22, lineHeight: 1.6 }}>
              {resultado.offline
                ? "No había conexión: se guardó en este celular y BAC la enviará sola apenas haya señal."
                : resultado.correo
                  ? `La ficha se está generando y se enviará por correo a ${resultado.correo}.`
                  : "La constancia quedó registrada. No se envió copia por correo porque no se dejó una dirección; la ficha se puede descargar desde la pestaña Disentimiento PAI."}
            </p>
            <div style={{ background: "#fff", border: "1px solid #DDE5E0", borderRadius: 14, padding: "14px 18px", fontSize: 13 }}>
              <FilaResumen label="Niño, niña o adolescente" valor={resultado.menor} />
              <FilaResumen label="Representante" valor={resultado.representante} />
              <FilaResumen label="Municipio" valor={resultado.municipio} />
              <FilaResumen label="Comunidad o barrio" valor={resultado.comunidad || "—"} ultima />
            </div>
            <div className="d-flex gap-2 justify-content-center mt-4 flex-wrap">
              <Button variant="outline-secondary" onClick={cerrar}>Cerrar</Button>
              <Button onClick={reiniciar} style={{ background: "#0E4F57", border: "none" }}>Registrar otra constancia</Button>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 520, margin: "0 auto" }}>
            {!online && (
              <div style={{ background: "#FFF4D6", color: "#8A6100", borderRadius: 12, padding: "10px 14px", fontSize: 13, marginBottom: 14, textAlign: "center" }}>
                📡 Sin conexión — la constancia se guardará en el dispositivo y se enviará sola cuando haya señal.
              </div>
            )}

            <Seccion numero={1} titulo="Datos de atención">
              {/* Automático: viene del territorio activo en BAC */}
              <div style={{ background: "#F2F7F6", border: "1.5px solid #DDE5E0", borderRadius: 12, padding: "10px 14px", marginTop: 12 }}>
                <div className="row g-2">
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: "#83958F", fontWeight: 600 }}>Municipio</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#16302E" }}>{municipio || "—"}</div>
                  </div>
                  <div className="col-6">
                    <div style={{ fontSize: 12, color: "#83958F", fontWeight: 600 }}>Comunidad o barrio</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#16302E" }}>{comunidad || "—"}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: "#83958F", marginTop: 6, lineHeight: 1.45 }}>
                  {municipio
                    ? "Se toma del territorio elegido en BAC. Si no es el correcto, cierra y usa 🔄 Cambiar territorio."
                    : "No hay territorio elegido: cierra este formulario y usa 🔄 Cambiar territorio."}
                </div>
              </div>
              <div className="row g-2">
                <div className="col-7">
                  <Campo label="Fecha">
                    <Form.Control type="date" value={f.fecha_atencion} max={fechaLocalHoy()} onChange={e => set("fecha_atencion", e.target.value)} />
                  </Campo>
                </div>
                <div className="col-5">
                  <Campo label="Hora">
                    <Form.Control type="time" value={f.hora_atencion} onChange={e => set("hora_atencion", e.target.value)} />
                  </Campo>
                </div>
              </div>
            </Seccion>

            <Seccion numero={2} titulo="Niño, niña o adolescente">
              <Campo label="Nombre completo">
                <Form.Control value={f.menor_nombre} onChange={e => set("menor_nombre", e.target.value)} placeholder="Nombre del niño, niña o adolescente" />
              </Campo>
              <Campo label="Documento de identidad (si tiene)">
                <Form.Control value={f.menor_documento} onChange={e => set("menor_documento", e.target.value)} placeholder="N.º documento" />
              </Campo>
              <div className="row g-2">
                <div className="col-6">
                  <Campo label="Edad">
                    <Form.Control type="number" inputMode="numeric" min={0} max={f.menor_edad_unidad === "Meses" ? 36 : 17} value={f.menor_edad} onChange={e => set("menor_edad", e.target.value)} placeholder="0"
                      onWheel={e => e.currentTarget.blur()} /* la rueda del mouse NO debe cambiar la edad al desplazarse */ />
                  </Campo>
                </div>
                <div className="col-6">
                  <Campo label="Unidad">
                    <Form.Select value={f.menor_edad_unidad} onChange={e => set("menor_edad_unidad", e.target.value)}>
                      <option value="Años">Años</option>
                      <option value="Meses">Meses</option>
                    </Form.Select>
                  </Campo>
                </div>
              </div>
            </Seccion>

            <Seccion numero={3} titulo="Madre, padre o representante legal">
              <Campo label="Nombre completo">
                <Form.Control value={f.representante_nombre} onChange={e => set("representante_nombre", e.target.value)} placeholder="Nombre y apellidos" />
              </Campo>
              <Campo label="Documento de identidad">
                <Form.Control value={f.representante_documento} onChange={e => set("representante_documento", e.target.value)} placeholder="N.º documento" />
              </Campo>
              <Campo label="Vínculo o calidad de representación">
                <Form.Select value={f.representante_vinculo} onChange={e => set("representante_vinculo", e.target.value)}>
                  <option value="">Selecciona...</option>
                  {VINCULOS.map(v => <option key={v} value={v}>{v}</option>)}
                </Form.Select>
              </Campo>
              {f.representante_vinculo === "Otro" && (
                <Campo label="¿Cuál?">
                  <Form.Control value={f.representante_vinculo_otro} onChange={e => set("representante_vinculo_otro", e.target.value)} placeholder="Ej: abuela con custodia" />
                </Campo>
              )}
              <Campo label="Teléfono de contacto">
                <Form.Control type="tel" value={f.representante_telefono} onChange={e => set("representante_telefono", e.target.value)} placeholder="300 000 0000" />
              </Campo>
              <Campo label="Correo electrónico (opcional)">
                <Form.Control type="email" value={f.representante_correo} onChange={e => set("representante_correo", e.target.value)} placeholder="correo@ejemplo.com" />
                <p style={{ fontSize: 11.5, color: "#83958F", margin: "6px 2px 0", lineHeight: 1.5 }}>
                  Si lo deja, se le envía una copia de la constancia. Si no, la constancia se registra igual.
                </p>
              </Campo>
            </Seccion>

            <Seccion numero={4} titulo="Vacunación ofrecida e información brindada">
              <Campo label="Vacuna(s) y dosis indicada(s) en esta visita">
                <Form.Control as="textarea" rows={3} value={f.vacunas_ofrecidas} onChange={e => set("vacunas_ofrecidas", e.target.value)} placeholder="Ej: Pentavalente, 2.ª dosis" />
              </Campo>
              <div style={{ fontSize: 12.5, lineHeight: 1.65, color: "#4C6360", background: "#E4F5F8", borderRadius: 12, padding: 14, marginTop: 14, textAlign: "center" }}>
                Yo, quien firma, declaro que el personal de salud revisó los antecedentes disponibles y me explicó, en lenguaje comprensible, para qué sirve la vacunación indicada, sus beneficios, posibles reacciones y cuidados posteriores, así como los riesgos de no vacunar en esta oportunidad. Pude hacer preguntas y recibí respuesta. Se me informó que puedo solicitar nuevamente la vacunación.
              </div>
            </Seccion>

            <Seccion numero={5} titulo="Decisión">
              <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#16302E", fontWeight: 700, background: "#FCE7DA", borderRadius: 12, padding: 14, textAlign: "center" }}>
                Después de recibir la información, NO acepto que se aplique(n) a mi hijo(a) o representado(a) la(s) vacuna(s) anotada(s) arriba en esta oportunidad.
              </div>
              <Campo label="Motivo de mi decisión">
                <Form.Control as="textarea" rows={4} value={f.motivo_decision} onChange={e => set("motivo_decision", e.target.value)} placeholder="Escriba aquí el motivo" />
              </Campo>
              <p style={{ fontSize: 12, lineHeight: 1.6, color: "#83958F", margin: "12px 0 0", textAlign: "center" }}>
                Entiendo que esta constancia registra mi decisión de hoy. No impide que solicite la vacunación más adelante ni reemplaza las acciones de seguimiento y protección que correspondan al personal de salud.
              </p>
            </Seccion>

            <Seccion numero={6} titulo="Constancia y firmas">
              <Campo label="Profesional o vacunador que informó">
                <Form.Control value={f.profesional_nombre} onChange={e => set("profesional_nombre", e.target.value)} placeholder="Nombre completo" />
              </Campo>
              <Campo label="Cargo">
                <Form.Control value={f.profesional_cargo} onChange={e => set("profesional_cargo", e.target.value)} placeholder="Ej: Enfermera vacunadora" />
              </Campo>

              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#4C6360", textAlign: "center", margin: "20px 0 8px" }}>
                Firma de la madre, el padre o representante — firme con el dedo dentro del recuadro
              </label>
              {/* Se oculta (no se desmonta) cuando no firma, para no perder su inicialización */}
              <div style={{ display: f.no_firma ? "none" : "block" }}>
                <div style={{ border: "1.5px solid #DDE5E0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                  <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: 170, touchAction: "none" }}
                    onMouseDown={empezarFirma} onMouseMove={moverFirma} onMouseUp={terminarFirma} onMouseLeave={terminarFirma}
                    onTouchStart={empezarFirma} onTouchMove={moverFirma} onTouchEnd={terminarFirma} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 0" }}>
                  <span style={{ fontSize: 12, color: "#83958F" }}>{hayFirma ? "Firma registrada" : "Aún no ha firmado"}</span>
                  <button type="button" onClick={limpiarFirma} style={{ fontSize: 12.5, fontWeight: 700, color: "#CC5A26", background: "none", border: "none", cursor: "pointer" }}>Borrar firma</button>
                </div>
              </div>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 14, cursor: "pointer" }}>
                <input type="checkbox" checked={f.no_firma} onChange={e => alternarNoFirma(e.target.checked)}
                  style={{ width: 18, height: 18, marginTop: 2, accentColor: "#2BA8C4", flexShrink: 0 }} />
                <span style={{ fontSize: 13, lineHeight: 1.5 }}>La madre, el padre o representante no firma</span>
              </label>

              {f.no_firma && (
                <div style={{ marginTop: 12, padding: "4px 14px 14px", border: "1.5px dashed #DDE5E0", borderRadius: 14 }}>
                  <Campo label="¿Se ofreció lectura y firma del formato?">
                    <div style={{ display: "flex", gap: 8 }}>
                      {["Si", "No"].map(v => (
                        <button key={v} type="button" onClick={() => set("se_ofrecio_lectura_firma", v)}
                          style={{
                            flex: 1, padding: 10, borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer",
                            border: `1.5px solid ${f.se_ofrecio_lectura_firma === v ? "#0E4F57" : "#DDE5E0"}`,
                            background: f.se_ofrecio_lectura_firma === v ? "#0E4F57" : "#fff",
                            color: f.se_ofrecio_lectura_firma === v ? "#fff" : "#4C6360",
                          }}>
                          {v === "Si" ? "Sí" : "No"}
                        </button>
                      ))}
                    </div>
                  </Campo>
                  <Campo label="Testigo">
                    <Form.Control value={f.testigo_nombre} onChange={e => set("testigo_nombre", e.target.value)} placeholder="Nombre completo del testigo" />
                  </Campo>
                </div>
              )}
            </Seccion>

            <p style={{ fontSize: 11, color: "#A9B6B1", textAlign: "justify", marginTop: 6, lineHeight: 1.55 }}>
              <strong>Fundamento normativo.</strong> Constitución Política, artículo 44 (derechos prevalentes de niños y niñas); Ley 1098 de 2006, artículos 8, 9, 29 y 39 (interés superior, prevalencia, esquema de vacunación en primera infancia y deber de cuidado familiar); Ley 1751 de 2015, artículo 10, literal d (información clara para decisiones libres e informadas); Ley 2406 de 2024, artículo 2, numeral 1 (acceso a vacunas y decisión libre e informada).
            </p>
          </div>
        )}
      </Modal.Body>

      {!resultado && (
        <Modal.Footer style={{ background: "#fff" }}>
          <Button variant="outline-secondary" onClick={cerrar} disabled={enviando}>Cancelar</Button>
          <Button onClick={guardar} disabled={enviando}
            style={{ background: "#E8703A", border: "none", fontWeight: 700, padding: "8px 20px" }}>
            {enviando ? "Guardando..." : online ? "Registrar constancia" : "Guardar en el dispositivo"}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

function Seccion({ numero, titulo, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 18, padding: "20px 18px", marginBottom: 14, boxShadow: "0 2px 14px rgba(14,79,87,0.07)" }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: "#2BA8C4", display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#0E4F57", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 800, flexShrink: 0 }}>{numero}</span>
        {titulo}
      </p>
      {children}
    </div>
  );
}
function Campo({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#4C6360", margin: "12px 0 6px" }}>{label}</label>
      {children}
    </div>
  );
}
function FilaResumen({ label, valor, ultima }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 0", borderBottom: ultima ? "none" : "1px solid #DDE5E0" }}>
      <span style={{ color: "#83958F" }}>{label}</span><span style={{ fontWeight: 600, textAlign: "right" }}>{valor}</span>
    </div>
  );
}
